import {
  json,
  redirect,
  type LinksFunction,
  type LoaderFunctionArgs,
} from "@remix-run/node";
import { Outlet, type UIMatch, useMatch } from "@remix-run/react";
import xyflowStyleSheetUrl from "@xyflow/react/dist/style.css?url";
import katexStyleSheetUrl from "katex/dist/katex.min.css?url";
import quillBubbleThemeUrl from "quill/dist/quill.bubble.css?url";
import quillSnowThemeUrl from "quill/dist/quill.snow.css?url";
import React, { useEffect, useState } from "react";
import Sidebar from "#app/components/layout/side-bar";
import { TourMount } from "#app/components/tours/tour-provider";
import { UserUIContext } from "#app/contexts/user-ui";
import { getUserId } from "#app/utils/auth.server.ts";
import { prisma } from "#app/utils/db.server.ts";

/** Signing out has to stay reachable for a user who is still mid-onboarding. */
const ONBOARDING_EXEMPT_PATHS = new Set(["/u/log-out"]);

/**
 * Onboarding gate for the whole authenticated app.
 *
 * The post-login redirect alone is not enough: a session can reach /u/* without
 * passing through it (SSO restart from a second tab after email verification,
 * or a direct URL), which let users skip the questionnaire entirely.
 */
export async function loader({ request }: LoaderFunctionArgs) {
  const userId = await getUserId(request);
  if (!userId) return json({});

  const url = new URL(request.url);
  if (ONBOARDING_EXEMPT_PATHS.has(url.pathname)) return json({});

  const onboarding = await prisma.userOnboarding.findUnique({
    where: { userId },
    select: { completedAt: true },
  });
  if (onboarding?.completedAt) return json({});

  const redirectTo = `${url.pathname}${url.search}`;
  throw redirect(
    `/user-onboarding?redirectTo=${encodeURIComponent(redirectTo)}`,
  );
}

export type GeneralHandle = {
  breadcrumb(match: UIMatch<unknown, unknown>): React.ReactNode;
  title(match: UIMatch<unknown, unknown>): string;
};

export const links: LinksFunction = () => {
  return [
    // No tour stylesheet: react-joyride carries its own inline styles and the
    // rest of the look is in tailwind.css. shepherd.js shipped a CSS file that
    // had to be linked here for the tour to be anything but unstyled.
    // Ensure Quill styles are available on hard reloads for any route that renders editors
    // (avoids route-scoped CSS only being present when navigating from a page that already loaded it)
    { rel: "stylesheet", href: quillSnowThemeUrl },
    { rel: "stylesheet", href: quillBubbleThemeUrl },
    // ?url → <link>, not an inline <style> (avoids Vite SSR/client CSS mismatch)
    { rel: "stylesheet", href: xyflowStyleSheetUrl },
    // `@streamdown/math` renders KaTeX markup but never loads its stylesheet.
    { rel: "stylesheet", href: katexStyleSheetUrl },
  ];
};

/**
 * Under /u the document never scrolls: the shell is viewport-height and its
 * regions scroll inside it. But the root element keeps the browser's
 * `overflow: auto`, so anything that reaches below the window for a moment —
 * a popover or menu rendered at body level, a card that grew — made the whole
 * app scrollable, and a `focus()` or `scrollIntoView()` near the bottom then
 * scrolled it: header and sidebar gone, white space below, and no scrollbar to
 * bring it back once the overflow was gone. The root is locked while the shell
 * is mounted, and a scroll that still reaches it is put back.
 */
function useLockedDocumentScroll() {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = "hidden";
    const putBack = () => {
      if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    };
    putBack();
    window.addEventListener("scroll", putBack, { passive: true });
    return () => {
      window.removeEventListener("scroll", putBack);
      root.style.overflow = previous;
    };
  }, []);
}

export default function UserSpace() {
  useLockedDocumentScroll();
  const alphaAgentIndexMatch = useMatch({
    path: "/u/alpha/agent",
    end: false,
  });
  const tutorialsMatch = useMatch({
    path: "/u/alpha/tutorials",
    end: false,
  });
  const marketplaceTryMatch = useMatch({
    path: "/u/marketplace/:workflowId/try",
    end: true,
  });
  // Policies is an app shell of its own (chat beside the policy pane): the
  // chat's message list scrolls itself, so the column must not scroll too —
  // otherwise a wheel over the chat drags the whole page along with it.
  const policiesMatch = useMatch({ path: "/u/policies", end: false });

  // Routes that own their own scrollport (chat, tutorials, marketplace try).
  // This is about overflow on the content column — not about hiding the rail.
  // Agent keeps the global sidebar (Recent lives there); only tutorials /
  // marketplace try / an explicit fullScreenLayout request hide the rail.
  const fullScreenMatch =
    alphaAgentIndexMatch !== null ||
    tutorialsMatch !== null ||
    marketplaceTryMatch !== null ||
    policiesMatch !== null;

  /**
   * Seeded `false`, NOT from `fullScreenMatch`.
   *
   * This is the IMPERATIVE flag — "a screen has asked for the whole page" (the
   * policy Decision flow). Which routes hide the rail is a separate question,
   * and `hideMainSidebar` below already answers it by naming them.
   *
   * Seeding it from the route conflated the two, and because this is state on
   * the `/u` layout — mounted once, never re-synced — it froze at whatever
   * route the layout first mounted on. `/u/alpha/agent` then rendered WITH the
   * rail when reached by client-side navigation from another `/u` page, and
   * WITHOUT it on a direct load, from the same URL. Landing after sign-in is a
   * direct load, so that inconsistency became the first screen every user saw.
   */
  const [fullScreenLayout, setFullScreenLayout] = useState<boolean>(false);

  /**
   * The rail is hidden by route, and by a screen that asks for the whole page.
   *
   * Agent chat and operator detail keep the rail unless a screen asks for the
   * whole page. Tutorials and marketplace try hide it by route.
   */
  const hideMainSidebar =
    tutorialsMatch !== null || marketplaceTryMatch !== null || fullScreenLayout;

  /**
   * Expanded by default, for the same reason as `fullScreenLayout` above: this
   * is mount-once state, so `!fullScreenMatch` froze the rail COLLAPSED on any
   * direct load of `/u/alpha/agent` and EXPANDED when the same URL was reached
   * by client-side navigation. The routes that want the whole page drop the
   * rail entirely via `hideMainSidebar`, so they never needed this to collapse
   * it as well. Collapsing stays available on the toggle.
   */
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <UserUIContext.Provider
      value={{
        fullScreenLayout,
        setFullScreenLayout,
        sidebarOpen,
        setSidebarOpen,
      }}
    >
      {/* The sidebar is a flex sibling now (it sizes itself between the
			    collapsed and expanded widths) rather than a fixed 64px rail the
			    content had to be padded around.

			    A real app shell: viewport-height, and the CONTENT scrolls rather
			    than the document. This was `min-h-screen` with
			    `overflow-x-hidden sm:overflow-x-auto`, which grew with its content
			    and let the whole page scroll — the sidebar included, so on a long
			    page (integrations) it slid out of view leaving white space below.

			    The sidebar's own `sm:sticky sm:top-0` was meant to prevent exactly
			    that and could never work: an ancestor with any non-`visible`
			    overflow becomes the sticky element's scrollport, so that
			    `overflow-x` made this div the scrollport — and since it was the
			    document scrolling, not this div, the sticky had nothing to stick
			    to. The same trap was quietly disabling `PageHeader`'s
			    `sticky top-0` on every page under here.

			    Horizontal overflow moves to the scroll container below, so wide
			    content still scrolls sideways — just without dragging the sidebar
			    along with it. */}
      <div className="flex h-dvh w-full overflow-hidden bg-[#FAFCFD] dark:bg-[#0E0E0E]">
        {hideMainSidebar ? null : <Sidebar />}

        {/* `min-h-0` is what allows a flex child to be shorter than its
				    content and therefore to scroll at all. Full-screen routes (the
				    chat, tutorials, the marketplace try panel) own their internal
				    scrolling, so they get no scrollport of their own here. */}
        <div
          className={`layout-right-block flex min-h-0 min-w-0 flex-1 flex-col ${
            fullScreenMatch || fullScreenLayout
              ? "overflow-hidden"
              : "overflow-auto"
          }`}
        >
          <Outlet />
        </div>

        {/* Renders nothing, and loads nothing, until a tour is started. Mounted
            here rather than inside the sidebar that starts it, so a tour survives
            the routes that hide the rail. */}
        <TourMount />
      </div>
    </UserUIContext.Provider>
  );
}
